import * as Sentry from '@sentry/browser';
import RSDK from '@sr/functions/rsdk/src';
import { DetailSection, Resume } from '@sr/types';
import React, { useCallback } from 'react';
import { Textarea } from 'theme-ui';
import { AddComboButton, Box, MarkedInput } from '../../ui';
import API from '../../utils/api';
import DateRange from './DateRange';
import getContentTypeLabel from './get-content-type-label';
import ItemFormContainer from './ItemFormContainer';
import LinkedinImportPrompt from './LinkedinImportPrompt';
import SectionFormContainer from './SectionFormContainer';
import Summary from './Summary';

type Props = {
  resume: Resume;
  section: DetailSection;
  isSetup?: boolean;
};

const DetailForm = ({ resume, section, isSetup }: Props) => {
  function createSectionItem() {
    API.sectionItem.create({ section: section });
    API.events.logEvent({
      userId: resume.owner,
      eventName: 'Resume Detail Item Added',
    });
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const path = `${resume.id}/sections/${section.sectionId}/items/${e.currentTarget.name}`;
    API.resume.setProperty({
      path,
      value: e.currentTarget.value,
    });
  }

  // Summary updated from RichText
  const updateSummary = useCallback(
    function updateSummary(itemId: string, value: string) {
      API.resume.setProperty({
        path: `${resume.id}/sections/${section.sectionId}/items/${itemId}/summary`,
        value,
      });
    },
    [resume.id, section.sectionId]
  );

  return (
    <SectionFormContainer
      resume={resume}
      sectionId={section.sectionId}
      isSetup={isSetup}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <>
          {(section.itemsOrder || []).map((itemId) => {
            const item = (section.items || {})[itemId];
            if (!item) {
              Sentry.captureException(
                new Error('Missing item from section.itemsOrder'),
                {
                  tags: {
                    resumeId: resume.id,
                    sectionId: section.sectionId,
                    itemId: itemId,
                    noFeedback: true,
                  },
                  fingerprint: ['missing-item'],
                }
              );
              return null;
            }
            // This should always be true. It is needed for Flow to be able to figure
            // out is is type deail.
            if (item.type === 'detail') {
              const itemPath = `sections/${section.sectionId}/items/${item.id}`;
              return (
                <ItemFormContainer
                  itemId={itemId}
                  section={section}
                  key={itemId}
                >
                  <Box sx={{ display: 'flex', flexWrap: ['wrap', 'nowrap'] }}>
                    <MarkedInput
                      placeholder={getContentTypeLabel(
                        'title',
                        section.contentType
                      )}
                      value={item.title}
                      name={`${item.id}/title`}
                      onChange={handleChange}
                      srId={RSDK.getItemPropSrId({
                        itemId: item.id,
                        sectionType: 'detail',
                        contentType: section.contentType,
                        prop: 'title',
                      })}
                      containerSx={{ width: ['100%', '50%'], mr: [0, 1] }}
                    />
                    <MarkedInput
                      placeholder={getContentTypeLabel(
                        'subtitle',
                        section.contentType
                      )}
                      value={item.subtitle}
                      name={`${item.id}/subtitle`}
                      onChange={handleChange}
                      srId={RSDK.getItemPropSrId({
                        itemId: item.id,
                        sectionType: 'detail',
                        contentType: section.contentType,
                        prop: 'subtitle',
                      })}
                      containerSx={{ width: ['100%', '50%'] }}
                    />
                  </Box>
                  {isSetup !== true && (
                    <MarkedInput
                      placeholder={getContentTypeLabel(
                        'website',
                        section.contentType
                      )}
                      value={item.website}
                      name={`${item.id}/website`}
                      onChange={handleChange}
                      type="url"
                      srId={RSDK.getItemPropSrId({
                        itemId: item.id,
                        sectionType: 'detail',
                        contentType: section.contentType,
                        prop: 'website',
                      })}
                    />
                  )}
                  <DateRange
                    startDate={item.startDate}
                    stopDate={item.stopDate}
                    isCurrent={item.isCurrent}
                    itemId={item.id}
                    path={`${resume.id}/${itemPath}`}
                    sectionType={section.type}
                    contentType={section.contentType}
                    currentDateString={resume.currentDateString}
                  />
                  {item.textType === 'markdown' ? (
                    <Summary
                      content={item.summary}
                      itemId={item.id}
                      contentType={section.contentType}
                      updateContent={updateSummary}
                    />
                  ) : (
                    <Textarea
                      placeholder="- Summary bullet points"
                      value={item.summary}
                      name={`${item.id}/summary`}
                      onChange={handleChange}
                      rows={4}
                      id={RSDK.getItemPropSrId({
                        itemId: item.id,
                        sectionType: 'detail',
                        contentType: section.contentType,
                        prop: 'summary',
                      })}
                    />
                  )}
                </ItemFormContainer>
              );
            } else {
              Sentry.captureException(
                new Error(
                  'DetailSectionItem should have same type (detail) as parent Section.'
                ),
                {
                  tags: {
                    resumeId: resume.id,
                    sectionId: section.sectionId,
                    itemId: itemId,
                  },
                }
              );
              return null;
            }
          })}
        </>
        {showLinkedinImportPrompt(resume, section) ? (
          <LinkedinImportPrompt
            resume={resume}
            addSectionItem={createSectionItem}
          />
        ) : null}
        {section.itemsOrder && section.itemsOrder.length > 0 && (
          <AddComboButton
            mb={7}
            mt={-5}
            ml={-4}
            onClick={createSectionItem}
            type="button"
            data-testid={`add-${section.contentType}`}
          >
            {getContentTypeLabel('add', section.contentType)}
          </AddComboButton>
        )}
      </form>
    </SectionFormContainer>
  );
};

function showLinkedinImportPrompt(
  resume: Resume,
  section: DetailSection
): boolean {
  // Only show for work experience
  if (section.contentType !== 'work-experience') return false;

  const numberOfJobs = section?.itemsOrder?.length ?? 0;
  if (numberOfJobs === 0) return true;
  if (numberOfJobs === 1 && isFirstItemEmpty(resume, section)) return true;

  return false;
}

function isFirstItemEmpty(resume: Resume, section: DetailSection): boolean {
  if (section.itemsOrder && section.items) {
    const item = section.items[section.itemsOrder[0]];
    if (item.title !== '') return false;
  }
  return true;
}

export default DetailForm;
